import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Responsive from "@amcharts/amcharts5/themes/Responsive";
import { EmptyState, Icons } from "construct-ui";
import chartModel from "../../../models/charts/chart.model";
import languageModel from "../../../models/language.model";
import 'CssFolder/chart.scss';

export default function NewModelDisplayClusteredBarChartComponent() {

    let chart;
    let chartId = 'xychart';
    let root;
    let colSeries1;
    let colSeries2;
    let xAxis;
    let yAxis;
    let legend;
    let data = null;
    let dataLoaded = false;
    let colorSet; // ["#01B3E3", "#B5B5B5", ... ]
    let DEFAULT_COLOR_SET = [
        am5.color(0x1428A0), // Blue
        am5.color(0xFE4335), // Orange
        am5.color(0xFEB545), // Yellow mustard
        am5.color(0x01B3E3), // Light blue
        am5.color(0x97D654), // Green
        am5.color(0x001737), // Black
        am5.color(0xB5B5B5), // Grey
    ]

    function initChart(_data) {
        createChart();
        createAxes();
        createSeries();
        createLegend();

        loadData(_data);

        // Animate on when appear
        colSeries1.appear(1000, 100);
        colSeries2.appear(1000, 100);
    }

    function createChart() {
        root = am5.Root.new(chartId);

        root.setThemes([
            am5themes_Responsive.new(root),
            am5themes_Animated.new(root)
        ]);

        chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelX: "none",
                wheelY: "none",
                layout: root.verticalLayout,
                paddingTop: 20,
                paddingBottom: 0,
                paddingLeft: 15,
                paddingRight: 15,
            })
        );

        // Set chart colors
        if (colorSet?.length > 0) {
            chart.get("colors").set("colors", colorSet);
        } else {
            chart.get("colors").set("colors", DEFAULT_COLOR_SET);
        }
    }

    function createAxes() {
        xAxis = chart.xAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: "week",
                renderer: am5xy.AxisRendererX.new(root, {
                    cellStartLocation: 0.1,
                    cellEndLocation: 0.9,
                    minGridDistance: 30
                })
            })
        );

        let xRenderer = xAxis.get("renderer");

        xRenderer.labels.template.setAll({
            fontSize: 12,
        });

        xRenderer.grid.template.setAll({
            stroke: am5.color(0xFFFFFF),
            strokeWidth: 1
        });

        yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                min: 0,
                maxPrecision: 0,
                renderer: am5xy.AxisRendererY.new(root, {})
            })
        );

        let yRenderer = yAxis.get("renderer");

        yRenderer.labels.template.setAll({
            fontSize: 12,
        });

        yRenderer.grid.template.setAll({
            stroke: am5.color(0xFFFFFF),
            strokeWidth: 1
        });
    }

    function createSeries() {
        // First Series
        colSeries1 = chart.series.push(
            am5xy.ColumnSeries.new(root, {
                name: languageModel.getText("LT00000283", "Target"),
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "totalTargetQuantity",
                categoryXField: "week"
            })
        );

        colSeries1.columns.template.setAll({
            tooltipText: "{valueY}",
            pointerOrientation: "horizontal",
            width: am5.percent(100),
        });

        colSeries1.data.processor = am5.DataProcessor.new(root, {
            numericFields: ["totalTargetQuantity"]
        });

        // Second Series
        colSeries2 = chart.series.push(
            am5xy.ColumnSeries.new(root, {
                name: languageModel.getText("LT00000284", "Actual"),
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "totalActualQuantity",
                categoryXField: "week"
            })
        );

        colSeries2.columns.template.setAll({
            tooltipText: "{valueY}",
            pointerOrientation: "horizontal",
            width: am5.percent(100),
        });

        colSeries2.data.processor = am5.DataProcessor.new(root, {
            numericFields: ["totalActualQuantity"]
        });
    }

    function loadData(_data = []) {
        if (data === _data) {
            return;
        }

        data = _data;
        dataLoaded = true;

        // load data to xAxis
        xAxis.data.setAll(_data);

        // load data to column series 1
        colSeries1.data.setAll(_data);

        // load data to column series 2
        colSeries2.data.setAll(_data);

        // to show / hide axis
        let xRenderer = xAxis.get("renderer");
        let yRenderer = yAxis.get("renderer");
        xRenderer.labels.template.set('visible', _data.length > 0);
        yRenderer.labels.template.set('visible', _data.length > 0);

        // load data to legend
        legend.data.setAll(_data.length > 0 ? chart.series.values : []);
    }

    function createLegend() {
        legend = chart.children.push(
            am5.Legend.new(root, {
                x: am5.percent(50),
                centerX: am5.percent(50),
                layout: root.horizontalLayout
            })
        );

        legend.labels.template.setAll({
            fontSize: 12,
            fontWeight: "300"
        });
    }

    return {
        oninit: function (vnode) {
            chartId += chartModel.generateID();
            colorSet = vnode.attrs.colorSet;
        },
        oncreate: function (vnode) {
            if (vnode.attrs.data) {
                initChart(vnode.attrs.data);
            }
        },
        onupdate: function (vnode) {
            if (vnode.attrs.data) {
                if (!dataLoaded) {
                    initChart(vnode.attrs.data);
                } else {
                    loadData(vnode.attrs.data);
                }
            }
        },
        view: function (vnode) {
            return [
                vnode.attrs.title &&
                m('.fw-bold.text-center', vnode.attrs.title),
                m('.chart', {
                    id: chartId,
                    style: {
                        'min-width': '320px'
                    }
                },
                    !(vnode.attrs.data?.length > 0) &&
                    m(EmptyState, {
                        icon: Icons.BAR_CHART_2,
                        content: languageModel.getText("LT00000317", "No results found"),
                        fill: true,
                        style: 'color: var(--is-inactive-gray)',
                    })
                )
            ]
        }
    }
}